/**
 * Copy and pure helpers for the landing trust region and FAQ.
 * Contact channels and the refund route come from the public-site policy.
 */

import {
  availableLegalLinkIds,
  contactChannelsWithValue,
  LEGAL_LINK_ROUTES,
  type ContactChannel,
} from "../PublicChrome/publicSitePolicy";

export const TRUST_HOURS = "Segunda a sexta, das 8h às 18h (horário de Brasília)";

export const REFUND_RULE =
  "Reembolso integral em até 7 dias da compra. Depois disso, cada pedido é analisado caso a caso.";

export const PAYMENT_LINES = [
  "Pagamento processado pelo Mercado Pago.",
  "PIX e cartão de crédito.",
] as const;

export const CLOSING_CONTROL_LABEL = "Testar uma questão";

export const REFUND_TITLE = "Reembolso e garantia";

export const PAYMENT_TITLE = "Pagamento";

export const CONTACT_TITLE = "Contato";

export const FAQ_HEADING = "Dúvidas frequentes";

export const TRUST_EYEBROW = "TRANSPARÊNCIA";

export const TRUST_TITLE = "Tudo claro antes de assinar";

export type FaqItem = {
  readonly id: string;
  readonly question: string;
  readonly paragraphs: readonly string[];
};

export const FAQ_ITEMS: readonly FaqItem[] = [
  {
    id: "conta",
    question: "Preciso criar uma conta para testar?",
    paragraphs: [
      "Não para a questão de exemplo: ela abre sem login. Para usar a plataforma completa, fazer simulados e salvar seus resultados, é preciso criar uma conta no Acertei.",
    ],
  },
  {
    id: "quantidade",
    question: "Quantas questões tem cada plano?",
    paragraphs: [
      "A quantidade de questões disponíveis varia de acordo com o plano escolhido. Consulte as informações de cada plano no momento da contratação ou entre em contato com o suporte do Acertei para saber qual opção melhor atende ao seu objetivo.",
    ],
  },
  {
    id: "reembolso",
    question: "Como funciona o reembolso?",
    paragraphs: [REFUND_RULE],
  },
  {
    id: "sincrono",
    question: "O que é um simulado síncrono?",
    paragraphs: [
      "É um simulado realizado online, ao vivo e em data e horário previamente definidos. Todos os participantes realizam a prova dentro do período programado. Por isso, o simulado síncrono não pode ser realizado em outro horário após o encerramento.",
    ],
  },
  {
    id: "pagamento",
    question: "Como é feito o pagamento?",
    paragraphs: [
      "O pagamento pode ser realizado diretamente pelo site, de acordo com o plano escolhido, ou solicitado por meio do suporte do Acertei.",
      "São aceitos PIX e cartão de crédito. Para pagamentos no cartão, consulte as condições de parcelamento disponíveis no momento da contratação.",
    ],
  },
];

function channelPhrase(channel: ContactChannel): string {
  switch (channel.id) {
    case "whatsapp":
      return "pelo WhatsApp";
    case "instagram":
      return `pelo Instagram ${channel.value}`;
    case "email":
      return `pelo e-mail ${channel.value}`;
    default: {
      const unreachable: never = channel.id;
      return unreachable;
    }
  }
}

export function trustContactSentence(): string {
  const phrases = contactChannelsWithValue().map(channelPhrase);
  if (phrases.length === 0) return "";
  if (phrases.length === 1) return `Fale com a gente ${phrases[0]}.`;
  const last = phrases[phrases.length - 1];
  const leading = phrases.slice(0, -1).join(", ");
  return `Fale com a gente ${leading} ou ${last}.`;
}

export function refundPolicyLink(): { readonly to: string; readonly label: string } | null {
  if (!availableLegalLinkIds().includes("reembolso")) return null;
  return {
    to: LEGAL_LINK_ROUTES.reembolso.to,
    label: "Ler a política de reembolso",
  };
}

export function toggleFaqIndex(open: ReadonlySet<number>, index: number): Set<number> {
  const next = new Set(open);
  if (next.has(index)) next.delete(index);
  else next.add(index);
  return next;
}
